import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldAlert, X, ArrowRight, CheckCircle, Clock, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';

export default function KYCBanner() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  // Only show for authenticated users that are not verified and not admin
  if (!user || user.role === 'admin' || user.role === 'manager') return null;
  if (user.kyc_status === 'verified') return null;
  if (dismissed) return null;

  const isPending = user.kyc_status === 'pending';
  const isRejected = user.kyc_status === 'rejected';

  return (
    <div className={`relative w-full flex items-center justify-between gap-4 px-8 py-2.5 text-[10px] font-black uppercase tracking-[0.1em] z-30 transition-all duration-500 animate-in slide-in-from-top ${
      isPending
        ? 'bg-amber-500/10 border-b border-amber-500/20 text-amber-300'
        : isRejected
        ? 'bg-rose-500/10 border-b border-rose-500/20 text-rose-300'
        : 'bg-indigo-500/10 border-b border-indigo-500/20 text-indigo-300'
    }`}>
      <div className="flex items-center gap-3">
        {isPending ? (
          <Clock size={14} className="text-amber-400 flex-shrink-0 animate-pulse" />
        ) : isRejected ? (
          <ShieldAlert size={14} className="text-rose-400 flex-shrink-0" />
        ) : (
          <ShieldCheck size={14} className="text-indigo-400 flex-shrink-0" />
        )}

        <span className="leading-none">
          {isPending
            ? t('kyc.status_pending_banner')
            : isRejected
            ? t('kyc.status_rejected_banner')
            : t('kyc.status_unverified_banner')}
        </span>
      </div>

      <div className="flex items-center gap-4 flex-shrink-0">
        {!isPending && (
          <Link
            to="/kyc"
            className={`flex items-center gap-2 px-5 py-1.5 rounded-full font-black text-[9px] uppercase tracking-widest transition-all shadow-lg ${
              isRejected
                ? 'bg-rose-500 text-white hover:bg-rose-600 shadow-rose-500/20'
                : 'bg-indigo-500 text-white hover:bg-indigo-600 shadow-indigo-500/20'
            }`}
          >
            {isRejected ? t('kyc.resubmit') : t('kyc.verify')}
            <ArrowRight size={10} />
          </Link>
        )}
        {isPending && (
          <div className="flex items-center gap-2 px-4 py-1.5 rounded-full text-[9px] font-black uppercase bg-amber-500/20 text-amber-300 border border-amber-500/20 tracking-widest">
            <CheckCircle size={10} />
            {t('kyc.pending')}
          </div>
        )}
        <button
          onClick={() => setDismissed(true)}
          className="p-1.5 rounded-lg hover:bg-white/10 transition-colors text-current opacity-40 hover:opacity-100"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
